import { ApiError, api, request, uploadFile } from './client';

// ─────────────────────────────────────────────────────────────
// Adding a book from the till: scan its ISBN, let the server fill in what it
// can find, type the price printed on the back, save.
//
// The same routes the Facility app's Add Book screen uses, so a book added at
// the counter is the same record as one added in the stockroom. The whole
// lookup lives on the backend (GET /catalog/books/lookup/isbn): our own
// catalogue first, then Google Books and Open Library, with any cover it finds
// re-hosted on our storage.
//
// Price and stock are never returned — no ISBN source carries the INR MRP
// printed on an Indian edition, so those two are always typed by hand.
// ─────────────────────────────────────────────────────────────

export type BindingType = 'PAPERBACK' | 'HARDCOVER' | 'SPIRAL';

export type LookupSource = 'CATALOG' | 'GOOGLE_BOOKS' | 'OPEN_LIBRARY';

export const SOURCE_LABELS: Record<LookupSource, string> = {
  CATALOG: 'our catalog',
  GOOGLE_BOOKS: 'Google Books',
  OPEN_LIBRARY: 'Open Library',
};

/**
 * A name the lookup found. `id` is set when it already exists in our
 * categories/authors/publishers, and null when staff must decide whether to
 * create it — the backend never creates master data on its own.
 */
export interface RefSuggestion {
  name: string;
  id: string | null;
}

export interface LookupBook {
  /** Always in English letters; the Malayalam original, if any, is `titleMl`. */
  title: string;
  titleMl: string | null;
  description: string;
  language: string;
  pages: number | null;
  publishedDate: string | null;
  bindingType: BindingType | null;
  authors: RefSuggestion[];
  publisher: RefSuggestion | null;
  /** Exactly one — the book's theme, not every heading it could be filed under. */
  category: RefSuggestion | null;
  coverImageUrl: string | null;
  imageUrls: string[];
}

/** Set when the ISBN is already catalogued — adding it again would fail. */
export interface ExistingBook {
  id: string;
  title: string;
  slug: string;
  coverImageUrl: string | null;
  isActive: boolean;
  stockQuantity: number;
  /** Optional — an older server does not send it. */
  isUsed?: boolean;
  /** What tells re-priced copies of one ISBN apart. Optional, as above. */
  mrp?: number;
  price?: number;
}

export interface IsbnLookupResult {
  isbn13: string;
  isbn10: string | null;
  found: boolean;
  sources: LookupSource[];
  existing: ExistingBook | null;
  /**
   * The used copy sharing this ISBN, beside the new book in `existing`. A
   * used copy keeps the new book's ISBN, so there can be one of each.
   */
  existingUsed: ExistingBook | null;
  /**
   * Every book under this ISBN — new, used, and each copy the publisher
   * re-priced — new first, lowest MRP first. Absent from an older server.
   */
  copies?: ExistingBook[];
  book: LookupBook | null;
  /**
   * Sources that could not be reached, as opposed to ones that looked and
   * found nothing. A book missing from Google Books is a book to type in; a
   * Google Books that answered 429 is a book to scan again in a few minutes.
   */
  unavailable: LookupSource[];
}

/**
 * The BLSM-A-00001 labels printed for books that have no ISBN. The backend
 * hands these out and answers a scan of one from the catalogue alone.
 */
export function isStandInIsbn(raw: string): boolean {
  return /^BLSM[\s_-]*A[\s_-]*\d+$/i.test(raw.trim());
}

/**
 * Local sanity check before spending a round trip. Mirrors the backend's
 * rule: a book barcode is a 978/979 EAN-13 (9790 is sheet music) or a
 * 10-character ISBN.
 */
export function looksLikeIsbn(raw: string): boolean {
  if (isStandInIsbn(raw)) return true;
  const cleaned = raw.replace(/[^0-9Xx]/g, '');
  if (cleaned.length === 13) return /^97[89]/.test(cleaned) && !cleaned.startsWith('9790');
  return cleaned.length === 10;
}

const asList = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

/**
 * Fill in anything the response left out, so a server a release behind the
 * till hands back a result the screen can still draw rather than an
 * `undefined.map` that takes it down.
 */
function normalizeLookup(raw: unknown, isbn: string): IsbnLookupResult {
  const r = (raw ?? {}) as Partial<IsbnLookupResult>;
  const book = r.book ?? null;
  const existing = r.existing ?? null;

  return {
    isbn13:
      r.isbn13 ||
      (isStandInIsbn(isbn) ? isbn.trim().toUpperCase() : isbn.replace(/[^0-9Xx]/g, '')),
    isbn10: r.isbn10 ?? null,
    found: r.found ?? Boolean(book || existing),
    sources: asList<LookupSource>(r.sources),
    existing,
    existingUsed: r.existingUsed ?? null,
    // Left undefined for an older server that does not send it: the screen
    // reads its absence as "this server cannot take price copies".
    copies: Array.isArray(r.copies) ? r.copies : undefined,
    book: book
      ? {
          ...book,
          authors: asList<RefSuggestion>(book.authors),
          publisher: book.publisher ?? null,
          category: book.category ?? null,
          imageUrls: asList<string>(book.imageUrls),
        }
      : null,
    unavailable: asList<LookupSource>(r.unavailable),
  };
}

export interface NamedRef {
  id: string;
  name: string;
}

export interface CreateBookPayload {
  title: string;
  /** The original Malayalam title. Searched, never displayed. */
  titleMl?: string;
  isbn10?: string;
  isbn13?: string;
  /** No ISBN: the server assigns the next stand-in, BLSM-A-00001, … */
  noIsbn?: boolean;
  description?: string;
  price: number;
  mrp: number;
  language?: string;
  pages?: number;
  publisherId?: string;
  publishedDate?: string;
  bindingType?: BindingType;
  /** Copies going on the shelf with it. The server books them into the shop. */
  stockQuantity?: number;
  isUsed?: boolean;
  isOnlineVisible?: boolean;
  isDCopy?: boolean;
  /** Tagged BEST SELLER on the storefront and carried in the homepage carousel. */
  isBestSeller?: boolean;
  coverImageUrl?: string;
  imageUrls?: string[];
  categoryIds?: string[];
  authorIds: string[];
  /** Lists this book against the others sharing the id ("Other options"). */
  groupId?: string;
  /**
   * Another copy of a book already here, at the publisher's new MRP. The ISBN
   * may then repeat; the server groups it with the copies it re-prices.
   */
  newPriceCopy?: boolean;
}

/**
 * What an edit sends: only the fields that changed. An untouched field sent
 * back would be rewritten. `stockQuantity` here is the figure the book should
 * have afterwards, not a number of copies to add — the server works out the
 * difference and records it as a stock movement.
 */
export type UpdateBookPayload = Partial<
  Omit<CreateBookPayload, 'publisherId' | 'coverImageUrl' | 'imageUrls'>
> & {
  /** Null clears it. */
  publisherId?: string | null;
};

/** A book as the staff detail route returns it, as much as the till reads. */
export interface ApiBook {
  id: string;
  /** Absent from a server older than the field; every row it knows is a book. */
  productType?: string;
  title: string;
  titleMl: string | null;
  isbn10: string | null;
  isbn13: string | null;
  sku: string | null;
  description: string | null;
  /** Decimal(10,2) — arrives as a string. Always coerce. */
  price: string | number;
  mrp: string | number;
  language: string | null;
  pages: number | null;
  /** The publisher's name, as text. */
  publisher: string | null;
  publisherId: string | null;
  publishedDate: string | null;
  bindingType: BindingType | null;
  stockQuantity: number;
  isUsed: boolean;
  /** Read as `!== false`: a server that predates it means an ordinary book. */
  isOnlineVisible?: boolean;
  isDCopy?: boolean;
  isBestSeller?: boolean;
  /** Shared by the other versions of this book — a used copy, another printing. */
  groupId?: string | null;
  coverImageUrl: string | null;
  categories?: { category: NamedRef }[];
  authors?: { author: NamedRef }[];
}

export interface CreatedBook {
  id: string;
  title: string;
  sku: string | null;
  isbn13: string | null;
}

const PAGE_SIZE = 100; // the backend's PaginationDto caps `limit` at 100

/**
 * Walk every page of a catalogue list so the pickers are complete. A short
 * page is the end-of-list signal: the pagination `meta` sits on the response
 * envelope, which the request layer has already unwrapped by the time the
 * rows get here.
 */
async function fetchAllPages(path: string): Promise<NamedRef[]> {
  const all: NamedRef[] = [];
  // Hard stop at 50 pages (5000 rows) guards against a pathological loop.
  for (let page = 1; page <= 50; page++) {
    const res = await api.get<NamedRef[] | { data?: NamedRef[] }>(
      `${path}?page=${page}&limit=${PAGE_SIZE}`,
    );
    const items = Array.isArray(res) ? res : (res?.data ?? []);
    all.push(...items.map(({ id, name }) => ({ id, name })));
    if (items.length < PAGE_SIZE) break;
  }
  return all;
}

/** The first page of names containing what was typed. */
async function searchByName(path: string, q: string, signal?: AbortSignal): Promise<NamedRef[]> {
  const res = await api.get<NamedRef[] | { data?: NamedRef[] }>(
    `${path}?page=1&limit=50&search=${encodeURIComponent(q)}`,
    signal,
  );
  const items = Array.isArray(res) ? res : (res?.data ?? []);
  return items.map(({ id, name }) => ({ id, name }));
}

/**
 * Create a catalogue entity, or select the one it collided with.
 *
 * The API matches on a canonical name key, so typing "penguin" when "Penguin"
 * exists comes back 409 with the record that is already there. For someone
 * cataloguing a book that is not a failure — they meant that record.
 *
 * A soft-deleted match is deliberately NOT reused: attaching a book to a
 * deleted author would quietly resurrect it in listings. The API's message
 * says to restore it first, so that error is left to surface.
 */
async function createOrReuse(path: string, name: string): Promise<NamedRef> {
  try {
    const created = await api.post<NamedRef>(path, { name });
    return { id: created.id, name: created.name };
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      const existing = (err.payload as { existing?: NamedRef & { isActive?: boolean } } | null)
        ?.existing;
      if (existing?.id && existing.isActive !== false) {
        return { id: existing.id, name: existing.name };
      }
    }
    throw err;
  }
}

export const catalogApi = {
  lookupIsbn: async (isbn: string): Promise<IsbnLookupResult> =>
    normalizeLookup(
      await request<unknown>(`/catalog/books/lookup/isbn?isbn=${encodeURIComponent(isbn)}`, {
        // Three upstream sources plus a cover download, so well past the
        // client's default on an ISBN nobody has looked up before.
        timeoutMs: 30_000,
      }),
      isbn,
    ),

  /** Upload a cover photo; the returned url is what goes on the book. */
  uploadCover: (file: File) =>
    uploadFile<{ url: string; thumbnailUrl?: string }>('/files/upload/book-cover', file),

  createBook: (payload: CreateBookPayload) => api.post<CreatedBook>('/catalog/books', payload),

  /**
   * Through the staff route: the public one answers 404 for a counter-only
   * book, which is exactly the kind the till most often needs to correct.
   */
  getBook: (id: string) => api.get<ApiBook>(`/catalog/books/admin/${encodeURIComponent(id)}`),

  updateBook: (id: string, payload: UpdateBookPayload) =>
    api.patch<CreatedBook>(`/catalog/books/${encodeURIComponent(id)}`, payload),

  /**
   * Ids of the books in the running flash sale, if there is one.
   *
   * The detail route stamps `price` with the sale price for these, so the
   * price it reports is not the book's own. Saving that back would burn the
   * sale price into the catalogue for good — the edit screen locks the price
   * instead. A missing or failing sale must never block an edit.
   */
  flashSaleBookIds: async (): Promise<string[]> => {
    try {
      const sale = await api.get<{ books?: { id?: string }[] } | null>(
        '/catalog/flash-sale/active',
      );
      return (sale?.books ?? []).flatMap((b) => (b?.id ? [b.id] : []));
    } catch {
      return [];
    }
  },

  /** Dozens, so the whole list is loaded and filtered on the till. */
  listCategories: () => fetchAllPages('/catalog/categories'),

  // Thousands of each, so these are asked for by what was typed rather than
  // loaded whole.
  searchAuthors: (q: string, signal?: AbortSignal) => searchByName('/catalog/authors', q, signal),
  searchPublishers: (q: string, signal?: AbortSignal) =>
    searchByName('/catalog/publishers', q, signal),

  createCategory: (name: string) => createOrReuse('/catalog/categories', name),
  createAuthor: (name: string) => createOrReuse('/catalog/authors', name),
  createPublisher: (name: string) => createOrReuse('/catalog/publishers', name),
};
